"use client";

import { useState } from "react";

/* ═══════════════════════════════════════════════════════════════════════════
   Auto mode route log — one task, every step tagged with the model that ran
   it. Mirrors the route tag the IDE, Code Agent and CLI show on each reply.
   The frontier model is deliberately unnamed: Auto's frontier pick changes
   as better models ship, and the apps always show the current one.
   ═══════════════════════════════════════════════════════════════════════════ */

type Profile = "balanced" | "max";

interface Step {
  model: "capuchin" | "frontier";
  reason?: string;
  text: string;
  path?: string;
  /** Only shown in Max quality (frontier plans and reviews every task) */
  maxOnly?: boolean;
}

const STEPS: Step[] = [
  { model: "frontier", reason: "planning", text: "Planned the change: a token bucket per API key, applied before the upload handler", maxOnly: true },
  { model: "capuchin", text: "Read the upload route and 4 related files", path: "src/routes/upload.ts" },
  { model: "capuchin", text: "Added rate-limit middleware and wired it in", path: "src/middleware/rateLimit.ts" },
  { model: "capuchin", text: "Ran the tests: 3 failed" },
  { model: "capuchin", text: "Tried a fix. Same 3 tests still fail" },
  { model: "frontier", reason: "tests failed", text: "Found the cause: a shared clock mock leaking between tests" },
  { model: "capuchin", text: "Applied the fix. 42 of 42 tests pass" },
  { model: "frontier", reason: "reviewing", text: "Reviewed the diff and flagged a missing Retry-After header", maxOnly: true },
  { model: "capuchin", text: "Added the header. Tests still pass", maxOnly: true },
];

const PROFILES: { id: Profile; label: string; note: string }[] = [
  { id: "balanced", label: "Auto mode", note: "Balanced: frontier steps in only when Capuchin struggles" },
  { id: "max", label: "Max quality", note: "The frontier model plans and reviews; Capuchin does the work in between" },
];

function RouteTag({ step }: { step: Step }) {
  const frontier = step.model === "frontier";
  return (
    <span
      className="inline-flex justify-self-start items-center gap-1.5 shrink-0 rounded-md px-2 py-0.5 text-[11.5px] font-semibold whitespace-nowrap"
      style={{
        background: frontier ? "rgba(108,92,231,0.09)" : "rgba(16,185,129,0.09)",
        color: frontier ? "var(--mc-violet)" : "#047857",
      }}
    >
      <span
        aria-hidden
        className="w-1.5 h-1.5 rounded-full"
        style={{ background: frontier ? "var(--mc-violet)" : "var(--mc-emerald)" }}
      />
      {frontier ? `Frontier · ${step.reason}` : "Capuchin"}
    </span>
  );
}

export default function AutoRouteLog() {
  const [profile, setProfile] = useState<Profile>("balanced");
  const steps = STEPS.filter((s) => profile === "max" || !s.maxOnly);
  const frontierCount = steps.filter((s) => s.model === "frontier").length;
  const active = PROFILES.find((p) => p.id === profile)!;

  return (
    <div className="premium-card overflow-hidden text-left" style={{ borderRadius: "18px" }}>
      {/* Header: the task and the profile switch, like the chat panel's model picker */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-[var(--border)]">
        <p className="text-[13px] text-(--text-secondary) min-w-0">
          <span className="font-semibold text-[var(--text-primary)]">Task:</span> add rate limiting to file uploads
        </p>
        <div role="group" aria-label="Auto profile" className="inline-flex rounded-lg p-0.5" style={{ background: "#F1F5F9" }}>
          {PROFILES.map((p) => (
            <button
              key={p.id}
              type="button"
              aria-pressed={profile === p.id}
              onClick={() => setProfile(p.id)}
              className="rounded-md px-3 py-1.5 text-[12px] font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--mc-violet)]"
              style={
                profile === p.id
                  ? { background: "#FFFFFF", color: "var(--text-primary)", boxShadow: "0 1px 2px rgba(15,23,42,0.08)" }
                  : { color: "var(--text-muted)" }
              }
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <ol className="divide-y divide-[var(--border)]" aria-live="polite">
        {steps.map((s, i) => (
          <li
            key={s.text}
            className="grid grid-cols-[1.5rem_1fr] sm:grid-cols-[1.5rem_auto_1fr] items-start gap-x-3 gap-y-1.5 px-5 py-3 motion-safe:animate-[route-in_280ms_ease-out_both]"
            style={{ background: s.model === "frontier" ? "rgba(108,92,231,0.025)" : undefined }}
          >
            <span className="text-[12px] tabular-nums text-(--text-muted) pt-0.5">{i + 1}</span>
            <RouteTag step={s} />
            <p className="col-start-2 sm:col-start-3 text-[13.5px] leading-snug text-[var(--text-primary)]">
              {s.text}
              {s.path && (
                <code className="block mt-1 text-[12px] text-(--text-muted)" style={{ fontFamily: "var(--font-geist-mono), monospace" }}>
                  {s.path}
                </code>
              )}
            </p>
          </li>
        ))}
      </ol>

      <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3.5 border-t border-[var(--border)] text-[12.5px]" style={{ background: "rgba(15,23,42,0.02)" }}>
        <span className="text-(--text-secondary)">{active.note}</span>
        <span className="font-semibold tabular-nums text-[var(--text-primary)]">
          {steps.length - frontierCount} of {steps.length} steps on Capuchin
        </span>
      </div>
    </div>
  );
}
